import {ScrollViewProvider} from 'components/Context/ScrollView';
import lodash from 'lodash';
import {FC, useEffect} from 'react';
import {twMerge} from 'tailwind-merge';

import {useCachedPDFContext} from '../context';

/**
 * Finds the closest number to a given value in an array.
 * For example, if the array is [0, 10, 20, 30] and the value is 15,
 * the function will return 10.
 */
const findPage = (_arr: number[], height: number): number => {
    const PADDING = 50;
    for (let i = 0; i < _arr.length; i++) {
        if (_arr[i] >= height + PADDING) {
            return Math.max(1, i);
        }
    }
    return Math.max(1, _arr.length);
};

type CachedPDFFlatlistProps = {
    className?: string;
    pages: number;
    renderPage: (props: {page: number}) => JSX.Element;
    renderPagePreview?: () => JSX.Element;
};
const CachedPDFFlatlist: FC<CachedPDFFlatlistProps> = ({
    className,
    pages,
    renderPage: Page,
    renderPagePreview: PagePreview,
}) => {
    const context = useCachedPDFContext();
    useEffect(() => {
        context._setMaxPages(pages);
    }, [context]);
    return (
        <div
            className={twMerge('relative flex flex-1 overflow-clip', className)}
        >
            <ScrollViewProvider
                className="absolute inset-0 flex flex-col items-center overflow-y-auto"
                id="cached-pdf-flatlist"
                onClick={() => {
                    if (context.showPagePreviews)
                        context._setShowPagePreviews(false);
                }}
                onScroll={() => {
                    const scrollRef = context.scrollRef;
                    if (scrollRef) {
                        const elementsTop = Array.from(
                            scrollRef.querySelectorAll<HTMLElement>(
                                `[data-page]`,
                            ),
                        ).map((el) => el.offsetTop);
                        const scrollY = scrollRef.scrollTop;
                        const pageIndex = findPage(elementsTop, scrollY);
                        if (pageIndex !== context.currentPage) {
                            context._setCurrentPage(pageIndex);
                        }
                    }
                }}
                ref={context._setRef}
            >
                <div className="flex w-full flex-col gap-2 bg-grey-200">
                    {lodash.range(1, pages + 1).map((page) => {
                        return <Page key={page} page={page} />;
                    })}
                </div>
            </ScrollViewProvider>
            {PagePreview && <PagePreview />}
        </div>
    );
};
export default CachedPDFFlatlist;
